import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { AdminAuctionSale } from '@nucleus-commerce/api-client/admin';
import { fromZonedInput } from '@nucleus-commerce/core';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { slugify } from '@/features/products';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { emptySaleForm, mapSaleField, toCreateSale } from '../api/sale-mappers';
import { useCreateSale } from '../hooks/use-sale-mutations';
import {
  saleFormSchema,
  type SaleFormInput,
  type SaleFormOutput,
} from '../schemas/sale-form-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'title',
  'handle',
  'startsAt',
  'firstCloseAt',
  'lotIntervalMinutes',
  'buyerPremiumPercent',
]);

export interface SaleCreateDialogProps {
  storeId: string;
  timeZone: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (sale: AdminAuctionSale) => void;
}

/**
 * The few fields a sale needs to exist: a title and address, when bidding opens, when the first
 * lot closes and how far apart the lots close. The essay, cover and lots follow on its page.
 */
export function SaleCreateDialog({
  storeId,
  timeZone,
  open,
  onOpenChange,
  onCreated,
}: SaleCreateDialogProps) {
  const { t } = useTranslation(['auctions', 'common']);
  const message = useErrorMessage();
  const create = useCreateSale(storeId);
  const [handleTouched, setHandleTouched] = useState(false);
  const form = useForm<SaleFormInput, unknown, SaleFormOutput>({
    resolver: useI18nResolver(
      saleFormSchema((value) => {
        const iso = fromZonedInput(value, timeZone);
        return iso ? Date.parse(iso) : null;
      }, true),
    ),
    defaultValues: emptySaleForm(timeZone),
  });

  const close = () => {
    onOpenChange(false);
    create.reset();
    form.reset(emptySaleForm(timeZone));
    setHandleTouched(false);
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      const sale = await create.mutateAsync(toCreateSale(values, timeZone));
      close();
      onCreated(sale);
    } catch (error) {
      if (
        applyFieldErrors(error, form.setError, {
          knownFields: KNOWN_FIELDS,
          mapField: mapSaleField,
        })
      ) {
        return;
      }
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('auctions:sales.create.title')}</DialogTitle>
          <DialogDescription>{t('auctions:sales.create.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auctions:sales.fields.title')}</FormLabel>
                  <FormControl>
                    <Input
                      autoFocus
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        if (!handleTouched) form.setValue('handle', slugify(e.target.value));
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="handle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auctions:sales.fields.handle')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="font-mono text-[13px]"
                      onChange={(e) => {
                        setHandleTouched(true);
                        field.onChange(e);
                      }}
                    />
                  </FormControl>
                  <FormDescription>{t('auctions:sales.fields.handleHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="startsAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auctions:sales.fields.startsAt')}</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="firstCloseAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auctions:sales.fields.firstCloseAt')}</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lotIntervalMinutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auctions:sales.fields.interval')}</FormLabel>
                    <FormControl>
                      <Input inputMode="numeric" {...field} />
                    </FormControl>
                    <FormDescription>{t('auctions:sales.fields.intervalHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="buyerPremiumPercent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auctions:fields.premium')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input inputMode="decimal" className="pr-8" {...field} />
                        <span
                          aria-hidden
                          className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm"
                        >
                          %
                        </span>
                      </div>
                    </FormControl>
                    <FormDescription>{t('auctions:sales.fields.premiumHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <p className="text-muted-foreground text-xs">
              {t('auctions:fields.timeZoneHint', { zone: timeZone })}
            </p>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? t('common:actions.saving') : t('auctions:sales.create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
